import React, { useState, useEffect } from 'react';

/**
 * ErrorDisplay Component
 * Provides comprehensive error display with categorization and recovery options
 */
const ErrorDisplay = ({
  error,
  onRetry,
  onCancel,
  onFallback,
  className='',
  showDetails = false,
  retryCount = 0,
  maxRetries = 3,
  retryButtonRef,
  fallbackButtonRef,
  cancelButtonRef
}) => {
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);

  // Handle automatic retry countdown
  useEffect(() => {
    if (error?.autoRetryIn && error.autoRetryIn > 0) {
      setRetryCountdown(error.autoRetryIn);
      const interval = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            if (onRetry) onRetry();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [error?.autoRetryIn, onRetry]);

  if (!error) return null;

  // Get error icon based on type
  const getErrorIcon = (type) => {
    switch (type) {
      case 'network':
        return (
          <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
          </svg>
        );
      case 'authentication':
        return (
          <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        );
      case 'resource':
        return (
          <svg className="w-16 h-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'server':
        return (
          <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
          </svg>
        );
      case 'rate_limit':
        return (
          <svg className="w-16 h-16 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'validation':
        return (
          <svg className="w-16 h-16 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
    }
  };

  // Get error title based on type
  const getErrorTitle = (type) => {
    switch (type) {
      case 'network':
        return 'Connection Issue';
      case 'authentication':
        return 'Authentication Required';
      case 'resource':
        return 'Resource Not Found';
      case 'server':
        return 'Server Error';
      case 'rate_limit':
        return 'Too Many Requests';
      case 'validation':
        return 'Validation Error';
      default:
        return 'Error Occurred';
    }
  };

  // Get suggested actions based on error type
  const getSuggestedActions = (type) => {
    switch (type) {
      case 'network':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Wait a moment and try again'
        ];
      case 'authentication':
        return [
          'Sign in again',
          'Clear browser cache and cookies',
          'Contact support if the issue persists'
        ];
      case 'resource':
        return [
          'Verify the parrot still exists',
          'Check if you have proper access permissions',
          'Return to the parrots list'
        ];
      case 'server':
        return [
          'Wait a few minutes and try again',
          'Check our status page for known issues',
          'Contact support if the problem continues'
        ];
      case 'rate_limit':
        return [
          'Wait before making another request',
          'Reduce the frequency of your actions',
          'Try again in a few minutes'
        ];
      case 'validation':
        return [
          'Check the provided information',
          'Ensure all required fields are filled',
          'Verify the data format is correct'
        ];
      default:
        return [
          'Try refreshing the page',
          'Wait a moment and try again',
          'Contact support if the issue persists'
        ];
    }
  };

  // Get error severity color
  const getSeverityColor = (type) => {
    switch (type) {
      case 'network':
        return 'orange';
      case 'authentication':
        return 'red';
      case 'resource':
        return 'yellow';
      case 'server':
        return 'red';
      case 'rate_limit':
        return 'amber';
      case 'validation':
        return 'purple';
      default:
        return 'red';
    }
  };

  const errorTitle = getErrorTitle(error.type);
  const suggestedActions = getSuggestedActions(error.type);
  const severityColor = getSeverityColor(error.type);
  const canRetry = error.canRetry && retryCount < maxRetries;

  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 ${className}`}
      role="alert"
      aria-labelledby="error-title"
      aria-describedby="error-message"
    >
      <div className="text-center space-y-6">
        {/* Error Icon */}
        <div 
          className="flex justify-center"
          aria-hidden="true"
        >
          {getErrorIcon(error.type)}
        </div>

        {/* Error Title and Message */}
        <div className="space-y-2">
          <h3 
            id="error-title"
            className="text-xl font-semibold text-gray-900 dark:text-white"
          >
            {errorTitle}
          </h3>
          <p 
            id="error-message"
            className="text-gray-600 dark:text-gray-400 max-w-md mx-auto dark:text-gray-300 dark:text-gray-600"
          >
            {error.message}
          </p>
        </div>

        {/* Error Code and Timestamp */}
        {(error.code || error.timestamp) && (
          <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            {error.code && (
              <p>Error Code: <span className="font-mono" data-testid="error-code">{error.code}</span></p>
            )}
            {error.timestamp && (
              <p>Time: {new Date(error.timestamp).toLocaleString()}</p>
            )}
          </div>
        )}

        {/* Retry Information */}
        {retryCount > 0 && (
          <div className={`bg-${severityColor}-50 dark:bg-${severityColor}-900/20 border border-${severityColor}-200 dark:border-${severityColor}-800 rounded-lg p-3`}>
            <p className={`text-sm text-${severityColor}-800 dark:text-${severityColor}-200`}>
              Retry attempt {retryCount} of {maxRetries} failed
            </p>
          </div>
        )}

        {/* Auto Retry Countdown */}
        {retryCountdown > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-center justify-center">
              <svg className="animate-spin w-4 h-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Retrying automatically in {retryCountdown} seconds...
              </p>
            </div>
          </div>
        )}

        {/* Fallback Notice */}
        {error.fallbackUsed && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <div className="flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Using default settings. Some data may be missing.
              </p>
            </div>
          </div>
        )}

        {/* Suggested Actions */}
        <div className="text-left max-w-md mx-auto">
          <h4 
            className="text-sm font-medium text-gray-900 dark:text-white mb-2"
            id="suggested-actions-title"
          >
            What you can try:
          </h4>
          <ul 
            className="text-sm text-gray-600 dark:text-gray-400 space-y-1 dark:text-gray-300 dark:text-gray-600"
            aria-labelledby="suggested-actions-title"
          >
            {suggestedActions.map((action, index) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-400 mr-2" aria-hidden="true">•</span>
                {action}
              </li>
            ))}
          </ul>
        </div>

        {/* Action Buttons */}
        <div 
          className="flex flex-col sm:flex-row justify-center gap-3"
          role="group"
          aria-label="Error recovery actions"
        >
          {/* Retry Button */}
          {canRetry && onRetry && (
            <button
              ref={retryButtonRef}
              type="button"
              onClick={onRetry}
              disabled={retryCountdown > 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
              aria-describedby="retry-button-help"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {retryCountdown > 0 ? `Retrying in ${retryCountdown}s` : 'Try Again'}
            </button>
          )}
          {canRetry && onRetry && (
            <div id="retry-button-help" className="sr-only">
              Press R key or click to retry loading. Keyboard shortcut: R
            </div>
          )}

          {/* Fallback Button */}
          {!canRetry && error.type !== 'authentication' && onFallback && (
            <button
              ref={fallbackButtonRef}
              type="button"
              onClick={onFallback}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-white"
              aria-describedby="fallback-button-help"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Use Defaults
            </button>
          )}
          {!canRetry && error.type !== 'authentication' && onFallback && (
            <div id="fallback-button-help" className="sr-only">
              Load with default settings. Keyboard shortcut: F
            </div>
          )}

          {/* Cancel/Back Button */}
          {onCancel && (
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:bg-gray-900 dark:text-gray-600"
              aria-describedby="cancel-button-help"
            >
              <svg 
                className="w-4 h-4 mr-2" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {error.type === 'authentication' ? 'Sign In' : 'Back to Parrots'}
            </button>
          )}
          {onCancel && (
            <div id="cancel-button-help" className="sr-only">
              {error.type === 'authentication' ? 'Go to sign in page' : 'Return to parrots list'}. Keyboard shortcut: Escape
            </div>
          )}
        </div>

        {/* Technical Details Toggle */}
        {showDetails && error.originalError && (
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none dark:text-gray-600"
            >
              {showTechnicalDetails ? 'Hide' : 'Show'} Technical Details
            </button>
            
            {showTechnicalDetails && (
              <div className="mt-3 text-left">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-xs font-mono text-gray-600 dark:text-gray-400 overflow-auto max-h-32 dark:text-gray-300 dark:text-gray-600">
                  <div className="space-y-1">
                    <div><strong>Error:</strong> <span data-testid="error-message">{error.originalError.message}</span></div>
                    {error.originalError.stack && (
                      <div><strong>Stack:</strong> <span data-testid="error-stack">{error.originalError.stack.split('\n')[0]}</span></div>
                    )}
                    {error.originalError.status && (
                      <div><strong>Status:</strong> <span data-testid="error-status">{error.originalError.status}</span></div>
                    )}
                    {error.originalError.url && (
                      <div><strong>URL:</strong> <span data-testid="error-url">{error.originalError.url}</span></div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorDisplay;
